import { Center, Table as ChakraTable } from '@chakra-ui/react';
import { Tooltip } from '@ui';
import type { ATCellsProps } from '../actionTable/ATTypes';
import { Box, Icon } from '@chakra-ui/react';
import { useATColumns } from '../hooks';
import { useRef, useEffect, useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

const ScrollableCell = <
  T extends Record<string, any>,
  TExtra extends Record<string, any> = {},
>({
  colKey,
  rowHeight,
  columnsConfig,
  item,
  virtualItem,
  renderCell,
  rowPropsForCell,
  renderContext,
}: any) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopArrow, setShowTopArrow] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const hasMoreContent = scrollHeight > clientHeight + 1;
    // Show top arrow when scrolled down
    setShowTopArrow(hasMoreContent && scrollTop > 3);
    // Show bottom arrow when there's more content below (always show if at top)
    setShowBottomArrow(
      hasMoreContent && scrollTop < scrollHeight - clientHeight - 3,
    );
  };

  useEffect(() => {
    // Multiple checks to ensure we catch the scroll state
    const checkMultipleTimes = () => {
      checkScroll();
      setTimeout(checkScroll, 10);
      setTimeout(checkScroll, 50);
      setTimeout(checkScroll, 100);
    };

    checkMultipleTimes();

    const element = scrollRef.current;
    if (element) {
      element.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(element);

      // Also check on mutation (content changes)
      const mutationObserver = new MutationObserver(checkScroll);
      mutationObserver.observe(element, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return () => {
        element.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }
  }, [item]);

  return (
    <Box
      ref={scrollRef}
      overflowY="auto"
      overflowX="hidden"
      maxH={rowHeight}
      height={'100%'}
      width={columnsConfig[colKey].width ?? '120px'}
      textWrap={'wrap'}
      textAlign={'center'}
      display="flex"
      flexDirection="column"
      justifyContent={'flex-start'}
      alignItems="center"
      fontSize={'xsm'}
      fontWeight={'medium'}
      css={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      {...rowPropsForCell?.[colKey]}
    >
      {renderCell
        ? renderCell({
            colKey,
            cellValue: item[colKey],
            rowValue: item as T & TExtra,
            rowIdx: virtualItem.index,
            context: renderContext,
          })
        : item[colKey] || ''}
      {/* Top arrow indicator */}
      {showTopArrow && (
        <Icon
          as={LuChevronUp}
          position="absolute"
          top="2px"
          left="50%"
          color="fg.subtle"
        />
      )}
      {/* Bottom arrow indicator */}
      {showBottomArrow && (
        <Icon
          as={LuChevronDown}
          position="absolute"
          bottom="2px"
          left="50%"
          color="fg.subtle"
        />
      )}
    </Box>
  );
};

export const TableCells = <
  T extends Record<string, any>,
  TExtra extends Record<string, any> = {},
  TContext = any,
>({
  item,
  rowHeight,
  virtualItem,
  renderCell,
  rowPropsForCell,
  renderContext,
}: ATCellsProps<T, TExtra, TContext>) => {
  const { columns, columnsConfig } = useATColumns();
  return (
    <>
      {columns.map((colKey) => {
        return (
          <ChakraTable.Cell
            alignContent="center"
            borderRight={'1px solid'}
            borderColor="border.subtle"
            key={`${String(colKey)}-${virtualItem.index}`}
            flex={1}
            _last={{
              borderRight: 'none',
            }}
            p={
              columnsConfig[colKey].width === '0px' ||
              columnsConfig[colKey].width === '0'
                ? '0px'
                : '3'
            }
            position="relative"
          >
            <ScrollableCell
              colKey={colKey}
              rowHeight={rowHeight}
              columnsConfig={columnsConfig}
              item={item}
              virtualItem={virtualItem}
              renderCell={renderCell}
              rowPropsForCell={rowPropsForCell}
              renderContext={renderContext}
            />
          </ChakraTable.Cell>
        );
      })}
    </>
  );
};
